import { useEffect, useMemo, useState } from "react";
import { Card } from "../../../../components";
import { BACKEND_AI_AGENT_URL } from "../../../../config";
import { SplittedProgressBar } from "./SplittedProgressBar/SplittedProgressBar";
import { PremiumSVG } from "../../../../assets";

type GetTokenDistributionApiResponse = {
  holdersChart: {
    // Only above 1k USD
    allHoldersCount: number;
    // Longer than 3 months
    longTermHoldersCount: number;
    // Shorter than 3 months
    shortTermHoldersCount: number;
  };
  whalesChart: {
    // Top 100 wallets.
    whalesCount: number;
    // Everyone outside top 100
    othersCount: number;
  };
  holders: {
    // Top 100 wallets + 100k USD in all assets combined.
    holders1kUSDTokenCount: number;
    // Everyone outside top 100 and users in top 100, bt without 100k USD in assets
    othersCount: number;
  };
  kols: {
    count: number;
    examples: {
      nickname: string;
      tokenBalance: string;
    }[];
  };
  bestTraders: {
    // The count of wallets with ROI > 20%
    count: number;
    top3Examples: {
      walletAddress: string;
      usdBalance: number;
      // 0.1 -> 10%
      roiRatio: number;
    }[];
  };
};

interface Props {
  projectId: string;
  tokenSymbol: string;
}

export const TokenDistribution = ({ tokenSymbol, projectId }: Props) => {
  const [data, setData] = useState<GetTokenDistributionApiResponse>();
  const [loading, setLoading] = useState(false);

  const longTermHoldersPercentage = useMemo(() => {
    if (!data) {
      return 0;
    }

    return (
      (data.holdersChart.longTermHoldersCount * 100) /
      data.holdersChart.allHoldersCount
    );
  }, [data]);

  const shortTermHoldersPercentage = useMemo(() => {
    if (!data) {
      return 0;
    }

    return (
      (data.holdersChart.shortTermHoldersCount * 100) /
      data.holdersChart.allHoldersCount
    );
  }, [data]);

  // const whaleHoldersPercentage = useMemo(() => {
  //   if (!data) {
  //     return 0;
  //   }

  //   return (
  //     (data.whalesChart.whalesCount * 100) / data.holdersChart.allHoldersCount
  //   );
  // }, [data]);

  // const nonWhaleHoldersPercentage = useMemo(() => {
  //   if (!data) {
  //     return 0;
  //   }

  //   return (
  //     (data.whalesChart.othersCount * 100) / data.holdersChart.allHoldersCount
  //   );
  // }, [data]);

  const seriousWhaleHoldersPercentage = useMemo(() => {
    if (!data) {
      return 0;
    }

    return (
      (data.holders.holders1kUSDTokenCount * 100) /
      data.holdersChart.allHoldersCount
    );
  }, [data]);

  const nonSeriousWhaleHoldersPercentage = useMemo(() => {
    if (!data) {
      return 0;
    }

    return (data.holders.othersCount * 100) / data.holdersChart.allHoldersCount;
  }, [data]);

  useEffect(() => {
    (async () => {
      setLoading(true);

      const response = await fetch(
        `${BACKEND_AI_AGENT_URL}/token-distribution?projectId=${projectId}`,
      );

      setData(await response.json());
      setLoading(false);
    })();
  }, [projectId]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!data || data.holdersChart.allHoldersCount === 0) {
    return <div>No data available</div>;
  }

  return (
    <div className="flex flex-1 flex-col gap-4">
      <div className="flex flex-col gap-1">
        <Card secondary disableWatermark className="px-[20px] py-[10px]">
          <div className="flex flex-col">
            <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px] text-[#FFFFFF88]">{`All holders (>1 USD)`}</span>
            <span className="font-ibm-plex-sans text-[20px] font-bold leading-[28px]">
              {data.holdersChart.allHoldersCount}
            </span>
          </div>
        </Card>
        <Card secondary disableWatermark className="px-[20px] py-[10px]">
          <div className="flex flex-1 flex-col gap-2">
            <div className="flex grow justify-between">
              <div className="flex flex-col">
                <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px]">
                  Long time holders
                </span>
                <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px] text-[#FFFFFF88]">{`> 3m`}</span>
              </div>
              <div className="flex flex-col items-end">
                <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px]">
                  Short time holders
                </span>
                <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px] text-[#FFFFFF88]">{`< 3m`}</span>
              </div>
            </div>
            <div className="flex grow">
              <SplittedProgressBar
                stages={[
                  {
                    color: "bg-blue-500",
                    width: `${longTermHoldersPercentage.toFixed(0)}%`,
                  },
                  {
                    color: "bg-gray-500",
                    width: `${shortTermHoldersPercentage.toFixed(0)}%`,
                  },
                ]}
              />
            </div>
            <div className="flex grow justify-between">
              <div className="flex flex-nowrap gap-2 items-center">
                <span className="font-ibm-plex-sans text-[16px] font-medium leading-[24px]">
                  {`${longTermHoldersPercentage.toFixed(2)}%`}
                </span>
                <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px] text-[#FFFFFF88]">
                  {data.holdersChart.longTermHoldersCount}
                </span>
              </div>
              <div className="flex flex-nowrap gap-2 items-center">
                <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px] text-[#FFFFFF88]">
                  {data.holdersChart.shortTermHoldersCount}
                </span>
                <span className="font-ibm-plex-sans text-[16px] font-medium leading-[24px]">
                  {`${shortTermHoldersPercentage.toFixed(2)}%`}
                </span>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* <div className="flex flex-col gap-1">
        <Card secondary className="px-[20px] py-[10px]">
          <div className="flex flex-col">
            <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px] text-[#FFFFFF88]">
              Whales
            </span>
            <span className="font-ibm-plex-sans text-[20px] font-bold leading-[28px]">
              {data.whalesChart.whalesCount}
            </span>
          </div>
        </Card>
        <Card secondary className="px-[20px] py-[10px]">
          <div className="flex flex-1 flex-col gap-2">
            <div className="flex grow justify-between">
              <div className="flex flex-col">
                <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px]">
                  Whale holdings
                </span>
              </div>
              <div className="flex flex-col items-end">
                <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px]">
                  Others
                </span>
              </div>
            </div>
            <div className="flex grow">
              <SplittedProgressBar
                stages={[
                  {
                    color: "bg-blue-500",
                    width: `${whaleHoldersPercentage.toFixed(0)}%`,
                  },
                  {
                    color: "bg-gray-500",
                    width: `${nonWhaleHoldersPercentage.toFixed(0)}%`,
                  },
                ]}
              />
            </div>
            <div className="flex grow justify-between">
              <div className="flex flex-nowrap gap-2 items-center">
                <span className="font-ibm-plex-sans text-[16px] font-medium leading-[24px]">
                  {`${whaleHoldersPercentage.toFixed(2)}%`}
                </span>
                <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px] text-[#FFFFFF88]">
                  {data.whalesChart.whalesCount}
                </span>
              </div>
              <div className="flex flex-nowrap gap-2 items-center">
                <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px] text-[#FFFFFF88]">
                  {data.whalesChart.othersCount}
                </span>
                <span className="font-ibm-plex-sans text-[16px] font-medium leading-[24px]">
                  {`${nonWhaleHoldersPercentage.toFixed(2)}%`}
                </span>
              </div>
            </div>
          </div>
        </Card>
      </div> */}
      <div className="flex flex-col gap-1">
        <Card
          disableWatermark
          secondary
          className="px-[20px] py-[10px] flex justify-between align-center"
        >
          <div className="flex flex-col">
            <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px] text-[#FFFFFF88]">
              {`Wallets holding > 1k USD in token`}
            </span>

            <span className="font-ibm-plex-sans text-[20px] font-bold leading-[28px]">
              {data.holders.holders1kUSDTokenCount}
            </span>
          </div>
          <div className="flex align-center justify-center h-full">
            <PremiumSVG />
          </div>
        </Card>
        <Card disableWatermark secondary className="px-[20px] py-[10px]">
          <div className="flex flex-1 flex-col gap-2">
            <div className="flex grow justify-between">
              <div className="flex flex-col">
                <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px]">
                  {`> 1k USD wallets`}
                </span>
              </div>
              <div className="flex flex-col items-end">
                <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px]">
                  Others
                </span>
              </div>
            </div>
            <div className="flex grow">
              <SplittedProgressBar
                stages={[
                  {
                    color: "bg-blue-500",
                    width: `${seriousWhaleHoldersPercentage.toFixed(0)}%`,
                  },
                  {
                    color: "bg-gray-500",
                    width: `${nonSeriousWhaleHoldersPercentage.toFixed(0)}%`,
                  },
                ]}
              />
            </div>
            <div className="flex grow justify-between">
              <div className="flex flex-nowrap gap-2 items-center">
                <span className="font-ibm-plex-sans text-[16px] font-medium leading-[24px]">
                  {`${seriousWhaleHoldersPercentage.toFixed(2)}%`}
                </span>
                <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px] text-[#FFFFFF88]">
                  {data.holders.holders1kUSDTokenCount}
                </span>
              </div>
              <div className="flex flex-nowrap gap-2 items-center">
                <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px] text-[#FFFFFF88]">
                  {data.holders.othersCount}
                </span>
                <span className="font-ibm-plex-sans text-[16px] font-medium leading-[24px]">
                  {`${nonSeriousWhaleHoldersPercentage.toFixed(2)}%`}
                </span>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
